import { useState, useEffect } from 'react';
import { locationApi } from '@/api/location.api';
import { cn } from '@/utils/cn';

interface LocationSelectProps {
  value?: string;  // locationId or ''
  onChange: (locationId: string) => void;
  disabled?: boolean;
  className?: string;
}

interface LocationOption { id: string; code: string; name: string; }

export const LocationSelect = ({ value = '', onChange, disabled, className }: LocationSelectProps) => {
  const [options, setOptions]   = useState<LocationOption[]>([]);
  const [loading, setLoading]   = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setHasError(false);
    locationApi.getList({ pageSize: 200, isActive: true })
      .then(res => {
        const items = res.data?.data?.items ?? [];
        setOptions(
          items.map((l: { id: string; code: string; name: string }) => ({
            id: l.id, code: l.code, name: l.name,
          }))
        );
      })
      .catch(err => {
        console.error('LocationSelect: failed to load locations', err);
        setHasError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const selectCls = cn(
    'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm',
    'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
    'focus:outline-none focus:ring-2 focus:ring-brand-500',
    (disabled || loading) && 'opacity-60 cursor-not-allowed',
    className
  );

  if (hasError) {
    return (
      <select disabled className={selectCls}>
        <option>(Error loading locations)</option>
      </select>
    );
  }

  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled || loading}
      className={selectCls}
    >
      <option value="">
        {loading ? 'Loading locations…' : 'All Locations'}
      </option>
      {options.map(o => (
        <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
      ))}
    </select>
  );
};
